import { FC, useEffect, useState } from 'react';
import { ErrorResponse, HashRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { appInsights } from './AppInsights';
import EnrollmentChecker from './Components/TermsOfUse/EnrollmentChecker';
import ConfirmContextProvider from './Contexts/ConfirmContextProvider';
import { useNotification } from './Contexts/NotificationContext';
import { useUserContext } from './Contexts/UserContext';
import { ActionTypes, ClientConfiguration, ConversationCreate, ConversationPatch, GptTypes } from './EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { useEnterpriseGPTApiCall } from './Hooks/useMiddlewareApiCall';
import { getInitialConversationPageSize } from './Hooks/useInfiniteConversations';
import { PageAdminSettings } from './Page/PageAdminSettings';
import { PageChat } from './Page/PageChat';
import { PageError } from './Page/PageError';
import { PageExploreGpts } from './Page/PageExploreGpts';
import { PageLayout } from './Page/PageLayout';
import { PagePersonalGpt } from './Page/PagePersonalGpt';
import { PageSettings } from './Page/PageSettings';
import { AdminSettingsRoute, ChatRoute, ChatSpecificRoute, ErrorRoute, ExploreGptRoute, PersonalGptRoute, PersonalGptSpecificRoute, SettingsRoute } from './Routes';
import { logout } from './Security/azureAdAuthentication';
import { setConversationsAction } from './store/actions/conversationActions';
import { setFeaturesAction } from './store/actions/featureActions';
import { setGptConfigurationsAction } from './store/actions/gptConfigurationsAction';
import { setSurveyAction } from './store/actions/surveyAction';
import { setUserPreferencesAction } from './store/actions/userPreferencesAction';
import { useRootState } from './store/hooks';
import { EventName_UserInteraction, EventProperty_Value_ApplicationType, EventProperty_Value_StartUp, UserInteractionEventProperties } from './store/models/customEvents';
import { isPWA } from './Utils/browser';
import { getUserInfoDebug } from './Utils/personaUtils';

export const Router: FC = () => {
    const { dispatch } = useRootState();
    const { dispatchNotification } = useNotification();
    const { userProfile } = useUserContext();
    const [conversationNotFoundError, setConversationNotFoundError] = useState(false);
    
    useEnterpriseGPTApiCall(async (client, abortSignal) => {
        let clientConfiguration: ClientConfiguration | null = null;
        
        try {
            clientConfiguration = await client.getClientConfigurationsOptions(abortSignal);
            console.log('clientConfiguration:', clientConfiguration);
            appInsights.trackTrace({ 
                message: 'clientConfiguration loaded; ' + getUserInfoDebug(userProfile),
                properties: clientConfiguration });

            dispatch(setFeaturesAction(clientConfiguration));
        } catch (e: unknown) {
            if ((e as Error).message !== 'canceled') {
                dispatchNotification('error.api.get.clientconfigurations', 'error');
            }
        }

        try {
            const survey = await client.getLatestSurvey(abortSignal);
            dispatch(setSurveyAction(survey));
        } catch (e) {
            dispatchNotification('error.api.get.survey', 'error');
        }

        try {
            const getuserPreferences = await client.getUserPreferences(abortSignal);
            // use this piece of code, in case you want to override the limited user preferences also on the DB

            // // Keep at most 4 GPTs (+ SyGPT) pinned
            // if (getuserPreferences.addedGpts?.length! >= 4) {
            //     // pick the last 3 added GPTs
            //     getuserPreferences.addedGpts = getuserPreferences.addedGpts?.slice(-3);
            // }
            dispatch(setUserPreferencesAction(getuserPreferences));

            const gptConfigurations = await client.getGptConfigurations(true, abortSignal);
            console.log('gptConfigurations:', gptConfigurations);
            appInsights.trackTrace({ 
                message: 'gptConfigurations loaded; ' + getUserInfoDebug(userProfile),
                properties: gptConfigurations });

            // Apply attachment configuration fallback for personal GPTs
            const gptConfigurationsWithFallback = gptConfigurations.map(gpt => {

                // If it's a personal GPT without its own attachment configuration, 
                // assign the global personal GPT attachment configuration
                if ((gpt.isPersonal) && clientConfiguration?.personalGpt?.attachmentConfiguration) {
                    // Use the personal GPT attachment configuration from client configuration
                    // This will ensure that personal GPTs have the correct attachment settings
                    const personalAttachmentConfig = clientConfiguration.personalGpt.attachmentConfiguration;
                    
                    // Check if personal GPT attachment configuration extensions are empty or null
                    if (!personalAttachmentConfig.extensions || personalAttachmentConfig.extensions.length === 0) {
                        // Find the default GPT to get its extensions
                        const defaultGpt = gptConfigurations.find(g => g.isDefault);
                        
                        if (defaultGpt?.attachment?.extensions && defaultGpt.attachment.extensions.length > 0) {
                            // Use default GPT's extensions with personal GPT's other attachment settings
                            return {
                                ...gpt,
                                attachment: {
                                    ...personalAttachmentConfig,
                                    extensions: defaultGpt.attachment.extensions
                                }
                            };
                        }
                    }
                    
                    return {
                        ...gpt,
                        attachment: personalAttachmentConfig
                    };
                }
                return gpt;
            });
            
            console.log('gptConfigurationsWithFallback:', gptConfigurationsWithFallback);
            appInsights.trackTrace({ 
                message: 'gptConfigurationsWithFallback; ' + getUserInfoDebug(userProfile),
                properties: gptConfigurationsWithFallback });

            dispatch(setGptConfigurationsAction(gptConfigurationsWithFallback));

            // Load initial conversations using getConversationsPaged with fullLoad = true
            // This loads the first page of conversations (limit will be determined by backend default)
            const conversationsPage = await client.getConversationsPaged(
                undefined, // projectId = null
                undefined, // no cursor (first page)
                getInitialConversationPageSize(),        // load first X conversations
                false,     // fullLoad = false (incremental loading)
                abortSignal
            );
            let conversations = conversationsPage.conversations ?? [];

            // Check if there's a conversation ID in the URL (e.g., #/chat/65b0d068-70fe-4dbd-b8cc-437963b306fb)
            const hash = window.location.hash;
            const chatRouteMatch = hash.match(/^#\/chat\/([a-f0-9-]{36})$/i);
            const urlConversationId = chatRouteMatch ? chatRouteMatch[1] : null;
            
            // If URL contains a specific conversation ID, load it
            if (urlConversationId) {
                try {
                    const urlConversation = await client.getConversationById(urlConversationId, abortSignal);
                    if (urlConversation) {
                        // Mark as fully loaded
                        (urlConversation as any).fullyLoaded = true;
                        // Check if this conversation is already in the list
                        const existingIndex = conversations.findIndex(c => c.id === urlConversationId);
                        if (existingIndex >= 0) {
                            // Replace the summary with full conversation
                            conversations[existingIndex] = urlConversation;
                        } else {
                            // Add it at the beginning if not in the list
                            conversations = [urlConversation, ...conversations];
                        }
                    } else {
                        // Conversation not found - show error notification
                        setConversationNotFoundError(true);
                    }
                } catch (err) {
                    console.warn('Failed to load conversation from URL:', urlConversationId, err);
                    // Show error notification when conversation doesn't exist
                    setConversationNotFoundError(true);
                    // Fall through to default behavior
                }
            } else {
                // Auto-load the most recent conversation (default behavior when no URL ID)
                if (conversations.length > 0) {
                    const fullConversation = await client.getConversationById(conversations[0].id, abortSignal);
                    if (fullConversation) {
                        // Mark as fully loaded
                        (fullConversation as any).fullyLoaded = true;
                        conversations[0] = fullConversation;
                    }
                }
            }

            // create an empty conversation, if not available, only for SyGPT
            const syGptConversations = conversations.filter(c => c.gpt?.type === GptTypes.SyGpt)
            const syGptLastConversation = syGptConversations.at(0);
            if (!syGptLastConversation || (syGptLastConversation?.prompts && syGptLastConversation.prompts.length > 0)) {
                const gpt = gptConfigurations.filter(g => g.type === GptTypes.SyGpt).at(0);
                const conversationCreate = new ConversationCreate({ gpt: { id: gpt?.id, type: GptTypes.SyGpt } });
                let newConversation = await client.createConversation(conversationCreate, abortSignal);
                newConversation = await client.patchConversation(
                    newConversation.id,
                    new ConversationPatch({ actionType: ActionTypes.RenameConversation })
                );
                // Insert at position 0 since it's the most recent conversation
                conversations = [newConversation, ...conversations];
            }

            const initialConversations = conversations.map((c) => ({ ...c, active: false, draft: false, prompts: c.prompts?.map((p) => ({ ...p, aborted: p.answer === null })) }));
            //console.log('Router.tsx - Initial conversations loaded:', initialConversations.length, 'nextCursor:', conversationsPage.nextCursor);
            dispatch(setConversationsAction(initialConversations, conversationsPage.nextCursor ?? undefined));
        } catch (e: unknown) {
            if ((e as ErrorResponse).status === 401) {
                dispatchNotification('error.api.common.expiredAuthentication', 'error', 'Login', logout);
            }
            else if ((e as Error).message !== 'canceled') {
                dispatchNotification('error.api.get.conversations', 'error');
            }
        }
    });

    useEffect(() => {
        if (userProfile) {
            const properties: UserInteractionEventProperties = {
                applicationType: EventProperty_Value_ApplicationType,
                userId: userProfile?.username,
                interactionType: EventProperty_Value_StartUp,
                interactionValue: isPWA() ? 'pwa' : 'browser'
            };

            appInsights.trackEvent({ name: EventName_UserInteraction, properties });
        }
    }, [userProfile]);

    // Show notification after component is fully mounted if conversation was not found
    // Use setTimeout to ensure the notification system is fully ready (after enrollment check, etc.)
    useEffect(() => {
        if (conversationNotFoundError) {
            const timer = setTimeout(() => {
                dispatchNotification('error.api.get.conversation', 'error');
                setConversationNotFoundError(false);
            }, 1000); // Wait 1 second to ensure UI is fully loaded
            
            return () => clearTimeout(timer);
        }
    }, [conversationNotFoundError, dispatchNotification]);

    // The EnrollmentChecker component is used to check if the user has accepted the terms of use
    return <>
        <HashRouter>
            <Routes>
                {/* Error page route - standalone without layout */}
                <Route path={ErrorRoute} element={<PageError />} />
                
                {/* Main app routes with layout */}
                <Route
                    element={
                        <EnrollmentChecker>
                            <ConfirmContextProvider>
                                <PageLayout menuDarkTheme>
                                    <Outlet />
                                </PageLayout>
                            </ConfirmContextProvider>
                        </EnrollmentChecker>
                    }
                >
                    <Route path={ChatRoute} element={<PageChat />} />
                    <Route path={ExploreGptRoute} element={<PageExploreGpts />} />
                    <Route path={PersonalGptRoute} element={<PagePersonalGpt />} />
                    <Route path={PersonalGptSpecificRoute} element={<PagePersonalGpt />} />
                    <Route path={ChatSpecificRoute} element={<PageChat />} />
                    <Route path={SettingsRoute} element={<PageSettings />} />
                    <Route path={AdminSettingsRoute} element={<PageAdminSettings />} />
                    <Route path='*' element={<Navigate to={ChatRoute} replace />} />
                </Route>
            </Routes>
        </HashRouter>
    </>;
};
